import { useState } from 'react';
import { Box, SimpleGrid, Stack, Group, Avatar, Badge, TextInput, AvatarGroup } from '@mantine/core';
import { IconSearch, IconPlus, IconLock, IconWorld, IconUsers } from '@tabler/icons-react';
import { Card, Text, Title, Button } from '../../../../components/ui';
import { mockChatGroups, ChatGroup } from '../mockData/chat';

const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
};

export default function GroupsPage() {
  const [search, setSearch] = useState('');

  const filteredGroups = mockChatGroups.filter(group =>
    group.name.toLowerCase().includes(search.toLowerCase()) ||
    group.description.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>Groups</Title>
          <Text c="dimmed" size="sm">{mockChatGroups.length} groups</Text>
        </Box>
        <Button leftSection={<IconPlus size={16} />}>Create Group</Button>
      </Group>

      {/* Search */}
      <Card>
        <TextInput
          placeholder="Search groups..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Card>

      {/* Groups Grid */}
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        {filteredGroups.map(group => (
          <Card key={group.id}>
            <Stack gap="md">
              <Group justify="space-between">
                <Group gap="sm">
                  <Avatar size="lg" radius="xl" color="blue">
                    <IconUsers size={20} />
                  </Avatar>
                  <Box>
                    <Group gap="xs">
                      <Text fw={600}>{group.name}</Text>
                      {group.isPrivate ? (
                        <IconLock size={14} color="var(--mantine-color-dimmed)" />
                      ) : (
                        <IconWorld size={14} color="var(--mantine-color-dimmed)" />
                      )}
                    </Group>
                    <Text size="sm" c="dimmed" lineClamp={1}>{group.description}</Text>
                  </Box>
                </Group>
                <Badge size="sm" variant="light" color={group.isPrivate ? 'red' : 'green'}>
                  {group.isPrivate ? 'Private' : 'Public'}
                </Badge>
              </Group>

              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="xs" c="dimmed">Members</Text>
                  <Text size="xs" c="dimmed">{group.memberIds.length} members</Text>
                </Group>
                <AvatarGroup>
                  {group.memberNames.slice(0, 5).map((name, index) => (
                    <Avatar key={index} size="sm" radius="xl" color="blue">
                      {name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </Avatar>
                  ))}
                  {group.memberIds.length > 5 && (
                    <Avatar size="sm" radius="xl" color="gray">
                      +{group.memberIds.length - 5}
                    </Avatar>
                  )}
                </AvatarGroup>
              </Stack>

              <Group justify="space-between">
                <Text size="xs" c="dimmed">Created {formatDate(group.createdAt)}</Text>
                <Button variant="light" size="xs">View Group</Button>
              </Group>
            </Stack>
          </Card>
        ))}
      </SimpleGrid>
    </Stack>
  );
}
